/**
 * Shared formatting utilities for TruHail application
 * Provides standardized formatting functions for dates, currency, and phone numbers
 */

/**
 * Format currency values using Brazilian Real (BRL) format
 * @param value - The numeric value to format
 * @returns Formatted currency string (e.g., "R$ 25,50")
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

/**
 * Format date values using Brazilian date format
 * @param date - Date string or Date object to format
 * @param options - Optional formatting options
 * @returns Formatted date string (e.g., "25/12/2024")
 */
export const formatDate = (
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string => {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };

  return new Intl.DateTimeFormat('pt-BR', options || defaultOptions).format(
    new Date(date)
  );
};

/**
 * Format date and time values using Brazilian format
 * @param date - Date string or Date object to format
 * @returns Formatted date and time string (e.g., "25/12/2024 14:30")
 */
export const formatDateTime = (date: string | Date): string => {
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date));
};

/**
 * Format phone numbers using Brazilian format
 * @param phone - Phone number string to format
 * @returns Formatted phone string (e.g., "(11) 99999-9999")
 */
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Check if it's a valid Brazilian phone number (11 digits)
  const match = cleaned.match(/^(\d{2})(\d{5})(\d{4})$/);
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }

  // Return original if doesn't match expected format
  return phone;
};

/**
 * Format CPF (Brazilian tax ID) with proper formatting
 * @param cpf - CPF string to format
 * @returns Formatted CPF string (e.g., "123.456.789-00")
 */
export const formatCPF = (cpf: string): string => {
  const cleaned = cpf.replace(/\D/g, '');
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{3})(\d{2})$/);
  if (match) {
    return `${match[1]}.${match[2]}.${match[3]}-${match[4]}`;
  }
  return cpf;
};

/**
 * Format distance values with appropriate units
 * @param distance - Distance in meters
 * @returns Formatted distance string (e.g., "1.5km" or "500m")
 */
export const formatDistance = (distance: number): string => {
  if (distance < 1000) {
    return `${Math.round(distance)}m`;
  }
  return `${(distance / 1000).toFixed(1)}km`;
};

/**
 * Format duration in minutes to human-readable format
 * @param minutes - Duration in minutes
 * @returns Formatted duration string (e.g., "1h 30min" or "45min")
 */
export const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0) {
    return `${hours}h ${mins}min`;
  }
  return `${mins}min`;
};

/**
 * Format time from Date object to HH:MM format
 * @param date - Date object to extract time from
 * @returns Formatted time string (e.g., "14:30")
 */
export const formatTime = (date: Date): string => {
  return new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

/**
 * Parse and format date string to ensure consistent formatting
 * @param dateString - Date string in various formats
 * @returns Standardized date string or original if invalid
 */
export const parseAndFormatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString;
    }
    return formatDate(date);
  } catch {
    return dateString;
  }
};